import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Spinner } from "../ui/spinner";
import {
  Skeleton,
  SkeletonCard,
  SkeletonTable,
  SkeletonKPICard,
} from "../ui/skeleton";
import { Alert, AlertDescription } from "../ui/alert";
import {
  EnhancedTabs,
  EnhancedTabsContent,
  EnhancedTabsList,
  EnhancedTabsTrigger,
} from "../ui/enhanced-tabs";
import { Badge } from "../ui/badge";
import { TooltipProvider } from "../ui/tooltip";
import {
  Plus,
  Gauge,
  TrendingUp,
  Folder,
  Shield,
  Settings,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { useUser } from "../../contexts/UserContext";
import { useCurrentContext } from "../../hooks/useContext";
import { MetadataService } from "../../services/metadataService";
import { SubCategoryService } from "../../services/subCategoryService";
import PageHeader from "../Common/PageHeader";
import MetadataList from "./MetadataList";
import CreateSeverityForm from "./CreateSeverityForm";
import CreateLikelihoodForm from "./CreateLikelihoodForm";
import CreateCategoryForm from "./CreateCategoryForm";
import CreateSubCategoryForm from "./CreateSubCategoryForm";
import EditSeverityForm from "./EditSeverityForm";
import EditLikelihoodForm from "./EditLikelihoodForm";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";
import UnifiedCategoriesManagement from "./UnifiedCategoriesManagement";

const OrganizationMetadataPage = () => {
  const { organizationId } = useParams();
  const { getAccessTokenSilently } = useAuth0();
  const { canManageOrganization } = useUser();
  const { currentContext } = useCurrentContext();

  const [activeTab, setActiveTab] = useState("categories");
  const [severities, setSeverities] = useState([]);
  const [likelihoods, setLikelihoods] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dialog states
  const [createSeverityOpen, setCreateSeverityOpen] = useState(false);
  const [createLikelihoodOpen, setCreateLikelihoodOpen] = useState(false);
  const [createCategoryOpen, setCreateCategoryOpen] = useState(false);
  const [createSubCategoryOpen, setCreateSubCategoryOpen] = useState(false);

  // Edit dialog states
  const [editSeverityOpen, setEditSeverityOpen] = useState(false);
  const [editLikelihoodOpen, setEditLikelihoodOpen] = useState(false);
  const [selectedSeverity, setSelectedSeverity] = useState(null);
  const [selectedLikelihood, setSelectedLikelihood] = useState(null);

  // Delete dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleteType, setDeleteType] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const canManage = canManageOrganization(organizationId);

  const fetchAllMetadata = async () => {
    try {
      setLoading(true);
      setError(null);

      const [
        severitiesData,
        likelihoodsData,
        categoriesData,
        subCategoriesData,
      ] = await Promise.all([
        MetadataService.getSeverities(organizationId, getAccessTokenSilently),
        MetadataService.getLikelihoods(organizationId, getAccessTokenSilently),
        MetadataService.getCategories(organizationId, getAccessTokenSilently),
        SubCategoryService.getSubCategories(
          organizationId,
          getAccessTokenSilently
        ),
      ]);

      setSeverities(severitiesData);
      setLikelihoods(likelihoodsData);
      setCategories(categoriesData);
      setSubCategories(subCategoriesData.sub_categories || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (organizationId) {
      fetchAllMetadata();
    }
  }, [organizationId, getAccessTokenSilently]);

  const handleSeverityCreated = () => {
    setCreateSeverityOpen(false);
    fetchAllMetadata();
  };

  const handleLikelihoodCreated = () => {
    setCreateLikelihoodOpen(false);
    fetchAllMetadata();
  };

  const handleCategoryCreated = () => {
    setCreateCategoryOpen(false);
    fetchAllMetadata();
  };

  const handleSubCategoryCreated = () => {
    setCreateSubCategoryOpen(false);
    fetchAllMetadata();
  };

  const handleCategoryAddClick = () => {
    setCreateCategoryOpen(true);
  };

  const handleSubCategoryAddClick = (categoryId = null) => {
    setCreateSubCategoryOpen(true);
  };

  // Edit handlers
  const handleEditSeverity = (severity) => {
    setSelectedSeverity(severity);
    setEditSeverityOpen(true);
  };

  const handleEditLikelihood = (likelihood) => {
    setSelectedLikelihood(likelihood);
    setEditLikelihoodOpen(true);
  };

  const handleSeverityUpdated = () => {
    setEditSeverityOpen(false);
    setSelectedSeverity(null);
    fetchAllMetadata();
  };

  const handleLikelihoodUpdated = () => {
    setEditLikelihoodOpen(false);
    setSelectedLikelihood(null);
    fetchAllMetadata();
  };

  // Delete handlers
  const handleDeleteSeverity = (severity) => {
    setDeleteItem(severity);
    setDeleteType("severity");
    setDeleteDialogOpen(true);
  };

  const handleDeleteLikelihood = (likelihood) => {
    setDeleteItem(likelihood);
    setDeleteType("likelihood");
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteItem || !deleteType) return;

    try {
      setDeleting(true);

      switch (deleteType) {
        case "severity":
          await MetadataService.deleteSeverity(
            deleteItem.id,
            getAccessTokenSilently
          );
          break;
        case "likelihood":
          await MetadataService.deleteLikelihood(
            deleteItem.id,
            getAccessTokenSilently
          );
          break;
        default:
          throw new Error("Invalid delete type");
      }

      setDeleteDialogOpen(false);
      setDeleteItem(null);
      setDeleteType(null);
      fetchAllMetadata();
    } catch (err) {
      console.error("Error deleting metadata:", err);
      setError("Failed to delete item. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setDeleteItem(null);
    setDeleteType(null);
  };

  if (loading) {
    return (
      <div className="p-10 w-full">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between mb-8">
          <div className="space-y-2">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-96" />
          </div>
          <div className="flex gap-3 flex-wrap">
            <Skeleton className="h-9 w-24" />
          </div>
        </div>

        {/* Stats Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {Array.from({ length: 3 }).map((_, index) => (
            <SkeletonKPICard key={index} />
          ))}
        </div>

        {/* Content Skeleton */}
        <SkeletonCard>
          <div className="space-y-6">
            <Skeleton className="h-10 w-full" />
            <div className="space-y-4">
              <Skeleton className="h-6 w-32" />
              <SkeletonTable rows={5} columns={4} />
            </div>
          </div>
        </SkeletonCard>
      </div>
    );
  }

  return (
    <div className="p-10 w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <PageHeader
          pageName="Metadata"
          entityName={currentContext.name}
          entityType="organization"
        />
        <div className="flex gap-3 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchAllMetadata}
            disabled={loading}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive" className="mb-8">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Permission Alert */}
      {!canManage && (
        <Alert className="mb-8">
          <Shield className="h-4 w-4" />
          <div>
            <h4 className="font-medium">Read-Only Access</h4>
            <p className="text-body-small text-muted-foreground mt-1">
              Only organization admins can create and manage metadata. All
              metadata is managed at the organization level and available for
              use across all business units.
            </p>
          </div>
        </Alert>
      )}

      {/* Metadata Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Categories
                </p>
                <p className="text-2xl font-bold">{categories.length}</p>
              </div>
              <Folder className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Severities
                </p>
                <p className="text-2xl font-bold">{severities.length}</p>
              </div>
              <Gauge className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Likelihoods
                </p>
                <p className="text-2xl font-bold">{likelihoods.length}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Metadata Content */}
      <Card className="border-0 shadow-none">
        <CardContent className="p-0">
          <TooltipProvider>
            <EnhancedTabs value={activeTab} onValueChange={setActiveTab}>
              <EnhancedTabsList variant="compact">
                <EnhancedTabsTrigger value="categories" icon={Folder}>
                  Categories & Sub-Categories
                </EnhancedTabsTrigger>
                <EnhancedTabsTrigger value="severities" icon={Gauge}>
                  Severities
                </EnhancedTabsTrigger>
                <EnhancedTabsTrigger value="likelihoods" icon={TrendingUp}>
                  Likelihoods
                </EnhancedTabsTrigger>
              </EnhancedTabsList>

              <EnhancedTabsContent value="categories" className="mt-6">
                <UnifiedCategoriesManagement
                  categories={categories}
                  subCategories={subCategories}
                  canManage={canManage}
                  organizationId={organizationId}
                  onCategoryAdd={handleCategoryAddClick}
                  onSubCategoryAdd={handleSubCategoryAddClick}
                  onDataUpdated={fetchAllMetadata}
                  getAccessTokenSilently={getAccessTokenSilently}
                />
              </EnhancedTabsContent>

              <EnhancedTabsContent value="severities" className="mt-6">
                <MetadataList
                  title="Severities"
                  items={severities}
                  type="severity"
                  canManage={canManage}
                  onAddNew={() => setCreateSeverityOpen(true)}
                  onEdit={handleEditSeverity}
                  onDelete={handleDeleteSeverity}
                />
              </EnhancedTabsContent>

              <EnhancedTabsContent value="likelihoods" className="mt-6">
                <MetadataList
                  title="Likelihoods"
                  items={likelihoods}
                  type="likelihood"
                  canManage={canManage}
                  onAddNew={() => setCreateLikelihoodOpen(true)}
                  onEdit={handleEditLikelihood}
                  onDelete={handleDeleteLikelihood}
                />
              </EnhancedTabsContent>
            </EnhancedTabs>
          </TooltipProvider>
        </CardContent>
      </Card>

      {/* Create Forms */}
      <CreateSeverityForm
        open={createSeverityOpen}
        onClose={() => setCreateSeverityOpen(false)}
        organizationId={organizationId}
        onSeverityCreated={handleSeverityCreated}
      />

      <CreateLikelihoodForm
        open={createLikelihoodOpen}
        onClose={() => setCreateLikelihoodOpen(false)}
        organizationId={organizationId}
        onLikelihoodCreated={handleLikelihoodCreated}
      />

      <CreateCategoryForm
        open={createCategoryOpen}
        onClose={() => setCreateCategoryOpen(false)}
        organizationId={organizationId}
        onCategoryCreated={handleCategoryCreated}
      />

      <CreateSubCategoryForm
        open={createSubCategoryOpen}
        onClose={() => setCreateSubCategoryOpen(false)}
        organizationId={organizationId}
        categories={categories}
        onSubCategoryCreated={handleSubCategoryCreated}
      />

      {/* Edit Forms */}
      <EditSeverityForm
        open={editSeverityOpen}
        onClose={() => setEditSeverityOpen(false)}
        severity={selectedSeverity}
        onSeverityUpdated={handleSeverityUpdated}
      />

      <EditLikelihoodForm
        open={editLikelihoodOpen}
        onClose={() => setEditLikelihoodOpen(false)}
        likelihood={selectedLikelihood}
        onLikelihoodUpdated={handleLikelihoodUpdated}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        loading={deleting}
        itemName={deleteItem?.description || deleteItem?.name}
        itemType={deleteType}
        warningMessage={
          deleteType === "severity"
            ? "Any risks using this severity level will need to be updated manually."
            : deleteType === "likelihood"
            ? "Any risks using this likelihood level will need to be updated manually."
            : null
        }
      />
    </div>
  );
};

export default OrganizationMetadataPage;
